/**
 * Test measurements (format v8): what each recorded value is compared with, and the rules that
 * report failed, out-of-range or missing tests. A measurement refers to a circuit by its letter
 * and to an RCD by its tag (see `Measurement`).
 *
 * Limits (every one `verified: false` until checked against the standard's text and with an
 * inspector; sources in docs/requirements/measurements.md):
 * - insulation: IEC 60364-6:2016 table 6.1 — ≥ 1 MΩ at 500 V DC (≤ 500 V circuits), ≥ 0.5 MΩ
 *   at 250 V (SELV/PELV);
 * - RCD: IEC 61008/61009 general type trips within 300 ms at IΔn (500 ms for selective "S"),
 *   and between 50 % and 100 % of IΔn on a ramp test;
 * - fault loop (TN): Zs × Ia ≤ U0, Ia the instantaneous trip current (5, 10, 20 × In for B, C, D);
 * - protective-conductor continuity: no fixed value in the standards; compared with the
 *   resistance of the conductor from its length and section (copper, ρ = 0,0225 Ω·mm²/m),
 *   else 2 Ω.
 */
import type { Element, Measurement, Project } from '@elec/domain';
import type { Analysis, CircuitInfo } from '@elec/analysis';
import { getSymbol } from '@elec/symbols';
import { circuitRcds, fmt, name, rule, sensitivityMa, type L, type Rule } from './core.ts';

export const DEFAULT_TEST_VOLTAGE_V = 500;
const U0 = 230;
const RHO_CU = 0.0225;

/** A device field, or the symbol's default for it. */
function field(e: Element, key: string): unknown {
  if (e.props[key] !== undefined && e.props[key] !== '') return e.props[key];
  return (getSymbol(e.symbolId)?.fields?.find((f) => f.key === key) as { default?: unknown } | undefined)
    ?.default;
}

/** Minimum insulation resistance (MΩ) for a test voltage. */
export const insulationMinMOhm = (testVoltageV = DEFAULT_TEST_VOLTAGE_V) => (testVoltageV <= 250 ? 0.5 : 1);

/** Longest trip time allowed at IΔn (ms): 500 for a selective RCD, 300 otherwise. */
export const rcdMaxTripMs = (rcd: Element) => (rcd.props.selective === true ? 500 : 300);

/** Highest fault-loop impedance (Ω) the circuit's breaker clears instantly, or undefined (fuse, unknown). */
export function loopMaxOhm(c: CircuitInfo): number | undefined {
  if (!c.ratingA || c.device.symbolId === 'iec.fuse') return undefined;
  const k = { B: 5, C: 10, D: 20 }[String(field(c.device, 'curve') ?? 'C')] ?? undefined;
  return k ? U0 / (k * c.ratingA) : undefined;
}

/** Highest protective-conductor resistance (Ω) expected for the circuit. */
export function continuityMaxOhm(c: CircuitInfo | undefined): number {
  const len = c?.circuit?.cable.lengthM;
  const s = c?.cable.sectionMm2;
  if (!len || !s) return 2;
  return Math.round((1.5 * ((RHO_CU * len) / s) + 0.1) * 100) / 100;
}

export type MeasureField =
  'insulationMOhm' | 'continuityOhm' | 'loopOhm' | 'tripMs' | 'tripMa' | 'testButtonOk';

export interface FieldVerdict {
  field: MeasureField;
  ok: boolean;
  /** Allowed range; both set for a band (RCD trip current), none for the test button. */
  min?: number;
  max?: number;
  unit: string;
}

/** A verdict's limit as shown: "≥ 1 MΩ", "≤ 300 ms", "15–30 mA" ('' for the test button). */
export function limitText(v: FieldVerdict, en = false): string {
  const n = (x: number) => (Math.round(x * 100) / 100).toLocaleString(en ? 'en-GB' : 'fr-BE');
  if (v.min !== undefined && v.max !== undefined) return `${n(v.min)}–${n(v.max)} ${v.unit}`;
  if (v.min !== undefined) return `≥ ${n(v.min)} ${v.unit}`;
  if (v.max !== undefined) return `≤ ${n(v.max)} ${v.unit}`;
  return '';
}

/**
 * Verdict of every value a measurement holds. `circuit` / `rcd` is what its reference points
 * to (undefined when nothing on the diagram has that letter or tag: no limit can be applied).
 */
export function measurementVerdicts(
  m: Measurement,
  target: { circuit?: CircuitInfo | undefined; rcd?: Element | undefined },
  earthing: string,
): FieldVerdict[] {
  const out: FieldVerdict[] = [];
  if (m.target === 'circuit') {
    if (m.insulationMOhm !== undefined) {
      const min = insulationMinMOhm(m.testVoltageV);
      out.push({ field: 'insulationMOhm', ok: m.insulationMOhm >= min, min, unit: 'MΩ' });
    }
    if (m.continuityOhm !== undefined) {
      const max = continuityMaxOhm(target.circuit);
      out.push({ field: 'continuityOhm', ok: m.continuityOhm <= max, max, unit: 'Ω' });
    }
    if (m.loopOhm !== undefined && earthing.startsWith('TN') && target.circuit) {
      const max = loopMaxOhm(target.circuit);
      if (max !== undefined) out.push({ field: 'loopOhm', ok: m.loopOhm <= max, max, unit: 'Ω' });
    }
  } else {
    const rcd = target.rcd;
    if (m.tripMs !== undefined && rcd) {
      const max = rcdMaxTripMs(rcd);
      out.push({ field: 'tripMs', ok: m.tripMs > 0 && m.tripMs <= max, max, unit: 'ms' });
    }
    if (m.tripMa !== undefined && rcd) {
      const idn = sensitivityMa(rcd);
      out.push({
        field: 'tripMa',
        ok: m.tripMa >= idn / 2 && m.tripMa <= idn,
        min: idn / 2,
        max: idn,
        unit: 'mA',
      });
    }
    if (m.testButtonOk !== undefined) out.push({ field: 'testButtonOk', ok: m.testButtonOk, unit: '' });
  }
  return out;
}

/** Circuits by letter and RCDs by tag, as measurements refer to them. */
export function measurementTargets(a: Analysis): {
  circuits: Map<string, CircuitInfo>;
  rcds: Map<string, Element>;
} {
  const circuits = new Map<string, CircuitInfo>();
  for (const c of a.circuits) if (c.code && !circuits.has(c.code)) circuits.set(c.code, c);
  const rcds = new Map<string, Element>();
  for (const r of circuitRcds(a)) if (r.tag && !rcds.has(r.tag)) rcds.set(r.tag, r);
  return { circuits, rcds };
}

/** The latest measurement of each circuit letter / RCD tag (later date, then later in the list). */
export function latestMeasurements(project: Project): {
  circuits: Map<string, Measurement>;
  rcds: Map<string, Measurement>;
} {
  const circuits = new Map<string, Measurement>();
  const rcds = new Map<string, Measurement>();
  for (const m of Object.values(project.measurements ?? {})) {
    const map = m.target === 'circuit' ? circuits : rcds;
    const old = map.get(m.ref);
    // Same day: a retest comes after what it repeats.
    if (!old || m.date > old.date || (m.date === old.date && old.retestOf !== m.id)) map.set(m.ref, m);
  }
  return { circuits, rcds };
}

const UNIT: Record<MeasureField, L> = {
  insulationMOhm: { fr: 'isolement', en: 'insulation', nl: 'isolatie' },
  continuityOhm: { fr: 'continuité PE', en: 'PE continuity', nl: 'continuïteit PE' },
  loopOhm: { fr: 'boucle de défaut', en: 'fault loop', nl: 'foutlus' },
  tripMs: { fr: 'temps de déclenchement', en: 'trip time', nl: 'uitschakeltijd' },
  tripMa: { fr: 'courant de déclenchement', en: 'trip current', nl: 'uitschakelstroom' },
  testButtonOk: { fr: 'bouton test', en: 'test button', nl: 'testknop' },
};

/** Failed values of the latest measurements, as findings of one rule's fields. */
function failures(fields: MeasureField[], severity?: 'error' | 'warning') {
  return ({ project, a }: { project: Project; a: Analysis }) => {
    const { circuits, rcds } = measurementTargets(a);
    const latest = latestMeasurements(project);
    const out = [];
    for (const [map, targets] of [
      [latest.circuits, circuits],
      [latest.rcds, rcds],
    ] as const)
      for (const m of map.values()) {
        const t = targets.get(m.ref);
        if (!t) continue;
        const target = m.target === 'circuit' ? { circuit: t as CircuitInfo } : { rcd: t as Element };
        const el = m.target === 'circuit' ? (t as CircuitInfo).device : (t as Element);
        for (const v of measurementVerdicts(m, target, project.meta.earthingSystem)) {
          if (v.ok || !fields.includes(v.field)) continue;
          const who = m.target === 'circuit' ? name(t as CircuitInfo) : m.ref;
          const text = (en: boolean, fail: string) =>
            v.field === 'testButtonOk'
              ? fail
              : ` ${fmt(m[v.field] as number, en)} ${v.unit} (${limitText(v, en)})`;
          out.push({
            targets: [el.id],
            sheetId: el.sheetId,
            ...(severity ? { severity } : {}),
            message: {
              fr: `${who} : ${UNIT[v.field].fr}${text(false, ' ne déclenche pas')}`,
              en: `${who}: ${UNIT[v.field].en}${text(true, ' does not trip')}`,
              nl: `${who}: ${UNIT[v.field].nl}${text(false, ' schakelt niet uit')}`,
            },
          });
        }
      }
    return out;
  };
}

export const MEASUREMENT_RULES: Rule[] = [
  rule(
    {
      id: 'R-TEST-01',
      severity: 'error',
      title: { fr: 'Résistance d’isolement', en: 'Insulation resistance', nl: 'Isolatieweerstand' },
      why: {
        fr: 'La résistance d’isolement mesurée doit atteindre au moins 1 MΩ sous 500 V (0,5 MΩ sous 250 V).',
        en: 'The measured insulation resistance must be at least 1 MΩ at 500 V (0.5 MΩ at 250 V).',
        nl: 'De gemeten isolatieweerstand moet minstens 1 MΩ bij 500 V bedragen (0,5 MΩ bij 250 V).',
      },
      fix: {
        fr: 'Recherchez le défaut (humidité, conducteur abîmé, appareil raccordé), réparez puis mesurez à nouveau.',
        en: 'Find the fault (moisture, damaged conductor, connected appliance), repair it and measure again.',
        nl: 'Zoek de fout (vocht, beschadigde geleider, aangesloten toestel), herstel en meet opnieuw.',
      },
      verified: false,
    },
    failures(['insulationMOhm']),
  ),
  rule(
    {
      id: 'R-TEST-02',
      severity: 'warning',
      title: {
        fr: 'Continuité du conducteur de protection',
        en: 'Protective conductor continuity',
        nl: 'Continuïteit van de beschermingsgeleider',
      },
      why: {
        fr: 'Une résistance plus élevée que celle du conducteur lui-même trahit une connexion mauvaise ou interrompue.',
        en: 'A resistance higher than the conductor’s own points to a poor or broken connection.',
        nl: 'Een hogere weerstand dan die van de geleider zelf wijst op een slechte of onderbroken verbinding.',
      },
      fix: {
        fr: 'Contrôlez les bornes de terre du circuit et resserrez-les, puis mesurez à nouveau.',
        en: 'Check and tighten the circuit’s earth terminals, then measure again.',
        nl: 'Controleer en span de aardklemmen van de kring aan, en meet opnieuw.',
      },
      verified: false,
    },
    failures(['continuityOhm']),
  ),
  rule(
    {
      id: 'R-TEST-03',
      severity: 'error',
      title: {
        fr: 'Impédance de boucle (TN)',
        en: 'Fault-loop impedance (TN)',
        nl: 'Foutlusimpedantie (TN)',
      },
      why: {
        fr: 'En schéma TN, la boucle de défaut doit permettre au disjoncteur de déclencher instantanément : Zs × Ia ≤ 230 V.',
        en: 'In a TN system the fault loop must let the breaker trip instantly: Zs × Ia ≤ 230 V.',
        nl: 'In een TN-stelsel moet de foutlus de automaat ogenblikkelijk laten uitschakelen: Zs × Ia ≤ 230 V.',
      },
      fix: {
        fr: 'Augmentez la section, raccourcissez le circuit, choisissez une courbe plus basse ou ajoutez un différentiel.',
        en: 'Increase the section, shorten the circuit, choose a lower curve or add an RCD.',
        nl: 'Verhoog de doorsnede, verkort de kring, kies een lagere curve of voeg een differentieel toe.',
      },
      verified: false,
    },
    failures(['loopOhm']),
  ),
  rule(
    {
      id: 'R-TEST-04',
      severity: 'error',
      title: { fr: 'Essai du différentiel', en: 'RCD test', nl: 'Test van het differentieel' },
      why: {
        fr: 'Un différentiel doit déclencher en 300 ms au plus à IΔn (500 ms s’il est sélectif), entre 50 % et 100 % de IΔn, et par son bouton test.',
        en: 'An RCD must trip within 300 ms at IΔn (500 ms if selective), between 50 % and 100 % of IΔn, and from its test button.',
        nl: 'Een differentieel moet binnen 300 ms uitschakelen bij IΔn (500 ms indien selectief), tussen 50 % en 100 % van IΔn, en via de testknop.',
      },
      fix: {
        fr: 'Remplacez le différentiel, puis refaites l’essai.',
        en: 'Replace the RCD, then test it again.',
        nl: 'Vervang het differentieel en test opnieuw.',
      },
      verified: false,
    },
    failures(['tripMs', 'tripMa', 'testButtonOk']),
  ),
  rule(
    {
      id: 'R-TEST-00',
      severity: 'info',
      title: { fr: 'Mesures à compléter', en: 'Measurements to complete', nl: 'Metingen aan te vullen' },
      why: {
        fr: 'Une fois les essais commencés, chaque circuit et chaque différentiel doivent avoir leurs mesures.',
        en: 'Once testing has started, every circuit and every RCD needs its measurements.',
        nl: 'Zodra de metingen begonnen zijn, heeft elke kring en elk differentieel zijn metingen nodig.',
      },
      fix: {
        fr: 'Menu Contrôle › « Mesures et essais » (ou l’onglet Contrôle du relevé) : complétez les valeurs manquantes.',
        en: 'Inspection menu › “Test results” (or the survey’s Checks tab): fill in the missing values.',
        nl: 'Menu Keuring › “Metingen en testen” (of het tabblad Controle van de opname): vul de ontbrekende waarden in.',
      },
      verified: true,
    },
    ({ project, a }) => {
      if (!Object.keys(project.measurements ?? {}).length) return [];
      const { circuits, rcds } = measurementTargets(a);
      const latest = latestMeasurements(project);
      const out = [];
      const missing = [...circuits].filter(([k]) => latest.circuits.get(k)?.insulationMOhm === undefined);
      if (missing.length)
        out.push({
          targets: missing.map(([, c]) => c.device.id),
          message: {
            fr: `Isolement non mesuré : ${missing.map(([k]) => k).join(', ')}`,
            en: `Insulation not measured: ${missing.map(([k]) => k).join(', ')}`,
            nl: `Isolatie niet gemeten: ${missing.map(([k]) => k).join(', ')}`,
          },
        });
      const untested = [...rcds].filter(([k]) => latest.rcds.get(k)?.tripMs === undefined);
      if (untested.length)
        out.push({
          targets: untested.map(([, r]) => r.id),
          message: {
            fr: `Différentiel non essayé : ${untested.map(([k]) => k).join(', ')}`,
            en: `RCD not tested: ${untested.map(([k]) => k).join(', ')}`,
            nl: `Differentieel niet getest: ${untested.map(([k]) => k).join(', ')}`,
          },
        });
      const orphans = [
        ...[...latest.circuits.keys()].filter((k) => !circuits.has(k)),
        ...[...latest.rcds.keys()].filter((k) => !rcds.has(k)),
      ];
      if (orphans.length)
        out.push({
          targets: [],
          severity: 'warning' as const,
          message: {
            fr: `Mesures sans circuit ni différentiel au schéma : ${orphans.join(', ')}`,
            en: `Measurements with no matching circuit or RCD on the diagram: ${orphans.join(', ')}`,
            nl: `Metingen zonder overeenkomstige kring of differentieel op het schema: ${orphans.join(', ')}`,
          },
        });
      return out;
    },
  ),
];
